<template>
  <uni-popup
    ref="popupRef"
    type="bottom"
    :mask-click="maskClick"
    background-color="#ffffff"
    borderRadius="20px 20px 0px 0px"
    @change="handleChange"
  >
    <view class="c-popup">
      <view class="c-popup__outer" :class="{ 'fixed-heihgt': fixedHeight }" :style="outerStyle">
        <view v-if="title" class="c-popup__title-wrap">
          <view class="c-popup__title-text">{{ title }}</view>
        </view>

        <view class="c-popup__close-wrap" @click.stop="handleImgClick">
          <c-img
            v-if="hasCloseButton"
            class="c-popup__close"
            :size="32"
            remote-name="close"
            stop-propagation
          />
        </view>

        <!-- 头部插槽 -->
        <slot name="headerSlot" />

        <!-- 内容区域 支持超过最大高度可滚动 当message传入时无效 -->
        <template v-if="!message">
          <view class="c-popup__content" :style="contentStyle">
            <slot />
          </view>
        </template>
        <!-- 消息提示 只在message传入的时候有效 -->
        <template v-else>
          <view class="c-popup__message">{{ message }}</view>
        </template>

        <!-- 基础按钮 -->
        <template v-if="hasBaseButton">
          <button-group :itemList="baseButtonGroupConfig" :margins="buttonsMargins" />
        </template>
        <template v-else>
          <!-- 尾部插槽 -->
          <slot name="footerSlot" />
        </template>
      </view>
    </view>
  </uni-popup>
</template>

<script setup lang="ts">
import ButtonGroup from '@/components/button-group/index.vue';
import {
  CANCEL_AUTO_CLOSE_EVENT,
  CANCEL_AUTO_CONFIRM_EVENT,
  CANCEL_EVENT,
  CHANGE_EVENT,
  CONFIRM_EVENT,
} from '@/constants';
import { useCustomPage } from '@/hooks/useCustomPage';
import { useAppStore } from '@/store';
import { ButtonGroupStatus, type AnyObj } from '@/types';
import { businessUtils } from '@/utils';
import { computed, onBeforeUnmount, ref, toRefs } from 'vue';

interface Props {
  /** 弹窗标题 */
  title?: string;
  /** 是否需要右上角关闭按钮 */
  hasCloseButton: boolean;
  /** 信息文本，和默认插槽互斥 */
  message?: string;
  /** 是否指定高度 */
  fixedHeight?: boolean;
  /** 是否需要底部安全区域 */
  safeArea?: boolean;
  /** 弹窗内容样式对象 */
  contentStyle?: AnyObj;
  /** 弹窗内容最外层样式对象 */
  outerStyle?: AnyObj;
  /** 基础按钮配置 */
  baseButtons?: any[];
  /** 确认按钮文本，只在baseButtons下生效 */
  baseConfirmText?: string;
  /** 取消按钮文本，只在baseButtons下生效 */
  baseCancelText?: string;
  /** 取消按钮文本状态主题，只在baseButtons下生效 */
  baseCancelButtonStatus?: ButtonGroupStatus;
  /** 按钮外边距 */
  buttonsMargins: number[];
  /** 是否禁用点击遮罩关闭 */
  maskClick?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  title: '',
  hasCloseButton: true,
  message: '',
  fixedHeight: false,
  baseButtons: () => [],
  buttonsMargins: () => [120, 28, 0, 28],
  baseConfirmText: '确认',
  baseCancelText: '取消',
  maskClick: true,
});
const {
  title,
  hasCloseButton,
  message,
  fixedHeight,
  contentStyle,
  outerStyle,
  baseButtons,
  baseConfirmText,
  baseCancelText,
  buttonsMargins,
  maskClick,
  baseCancelButtonStatus,
} = toRefs(props);

const customHook = useCustomPage();
const appStore = useAppStore();

const emits = defineEmits([CANCEL_EVENT, CONFIRM_EVENT, CHANGE_EVENT]);

const hasBaseButton = !!baseButtons.value.length;

const popupRef = ref(null);

// 基础按钮组的配置信息
const baseButtonGroupConfig = computed(() => {
  const buttons = [];

  if (baseButtons.value.includes(CANCEL_AUTO_CLOSE_EVENT)) {
    buttons.push({
      text: baseCancelText.value,
      status: baseCancelButtonStatus.value || ButtonGroupStatus.disabled,
      onClick: () => close(),
    });
  }

  if (baseButtons.value.includes(CANCEL_AUTO_CONFIRM_EVENT)) {
    buttons.push({
      text: baseConfirmText.value,
      status: ButtonGroupStatus.activeFill,
      onClick: () => close(),
    });
  }

  if (baseButtons.value.includes(CANCEL_EVENT)) {
    buttons.push({
      text: baseCancelText.value,
      status: ButtonGroupStatus.disabled,
      onClick: () => handleBaseButtonCancel(),
    });
  }

  if (baseButtons.value.includes(CONFIRM_EVENT)) {
    buttons.push({
      text: baseConfirmText.value,
      status: ButtonGroupStatus.activeFill,
      onClick: () => handleBaseButtonConfirm(),
    });
  }

  return buttons;
});

const handleBaseButtonCancel = () => emits(CANCEL_EVENT);

const handleBaseButtonConfirm = () => emits(CONFIRM_EVENT);

const handleChange = (e: AnyObj) => {
  appStore.displayPageMeta = e.show;
  if (e.show) {
    emits(CHANGE_EVENT, e.show);
  } else {
    setTimeout(() => {
      emits(CHANGE_EVENT, e.show);
    }, 200);
  }
};

const handleImgClick = () => {
  close();
  if (customHook.isCustomPage) {
    businessUtils.matomo.customPageClick();
  }
};

const open = () => popupRef.value?.open();

const close = () => popupRef.value?.close();

defineExpose({
  open,
  close,
});

/** 组件卸载前，把滚动锁定标识置为false */
onBeforeUnmount(() => {
  appStore.displayPageMeta && (appStore.displayPageMeta = false);
});
</script>

<style scoped lang="scss">
.c-popup {
  position: relative;
  z-index: 9991;
  .c-popup__outer {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    max-height: 80vh;
    background: #ffffff;
    border-radius: 40rpx 40rpx 0 0;

    padding-bottom: var(--window-bottom);


    &.fixed-heihgt {
      height: 75vh;
    }
  }

  .c-popup__title-wrap {
    position: relative;
    z-index: 99;
    flex-shrink: 0;
    width: 100%;
    height: 124rpx;
    border-radius: 40rpx 40rpx 0 0;

    .c-popup__title-text {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      font-size: 36rpx;
      line-height: 44rpx;
      font-weight: bold;
    }
  }

  .c-popup__close-wrap {
    position: absolute;
    z-index: 101;
    top: 26rpx;
    right: 20rpx;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 64rpx;
    height: 64rpx;

    .c-popup__close {
      width: 32rpx;
      height: 32rpx;
    }
  }

  .c-popup__content {
    flex: 1;
    overflow-x: hidden;
    overflow-y: scroll;
    padding: 0 28rpx;
    // min-height: 120rpx;
  }

  .c-popup__message {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24rpx 24rpx 120rpx;
    font-size: 28rpx;
    color: $N7;
  }
}
</style>
